import Head from "next/head";
import Link from "next/link";
import { ArrowLeft, ArrowRight, Clock3 } from "lucide-react";
import {
  type LnatSeoArticle,
  getLnatSeoArticleBySlug,
} from "@/lib/lnat/seoArticles";

interface LnatSeoArticlePageProps {
  article: LnatSeoArticle;
}

export function LnatSeoArticlePage({ article }: LnatSeoArticlePageProps) {
  const relatedArticles = article.relatedSlugs
    .map((slug) => getLnatSeoArticleBySlug(slug))
    .filter((item): item is LnatSeoArticle => item !== null);

  return (
    <>
      <Head>
        <title>{`${article.title} | Durmah LNAT`}</title>
        <meta name="description" content={article.description} />
        <link key="route-canonical" rel="canonical" href={`https://www.durmah.ai/lnat/${article.slug}`} />
        <meta property="og:title" content={`${article.title} | Durmah LNAT`} />
        <meta property="og:description" content={article.description} />
      </Head>

      <main className="min-h-screen bg-white dark:bg-[#0B1412] text-gray-900 dark:text-white">
        <article className="mx-auto w-full max-w-4xl px-4 py-14 sm:px-6 lg:px-8 lg:py-20">
          <div className="mb-8 flex flex-wrap items-center gap-3 text-sm font-semibold">
            <Link
              href="/lnat/articles"
              className="inline-flex items-center gap-1 text-purple-600 hover:text-purple-700 dark:text-purple-300 dark:hover:text-purple-200"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to LNAT Articles
            </Link>
            <span className="text-gray-300 dark:text-white/20">/</span>
            <Link
              href="/lnat-preparation"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              LNAT Preparation
            </Link>
            <span className="text-gray-300 dark:text-white/20">/</span>
            <Link
              href="/lnat/dashboard"
              className="text-gray-600 hover:text-gray-900 dark:text-gray-300 dark:hover:text-white"
            >
              LNAT HQ
            </Link>
          </div>

          <header className="mb-10">
            <div className="inline-flex items-center gap-2 rounded-full border border-purple-200 bg-purple-50 px-3 py-1 text-xs font-black uppercase tracking-wider text-purple-700 dark:border-purple-400/30 dark:bg-purple-500/10 dark:text-purple-200">
              <Clock3 className="h-3.5 w-3.5" />
              {article.readTime}
            </div>
            <h1 className="mt-4 text-3xl font-black tracking-tight text-gray-900 dark:text-white sm:text-5xl">
              {article.title}
            </h1>
            <p className="mt-4 text-lg leading-relaxed text-gray-600 dark:text-gray-300">
              {article.summary}
            </p>
          </header>

          <div className="space-y-10">
            {article.sections.map((section) => (
              <section key={section.heading} className="space-y-4">
                <h2 className="text-2xl font-black tracking-tight text-gray-900 dark:text-white">
                  {section.heading}
                </h2>
                {section.paragraphs.map((paragraph) => (
                  <p
                    key={paragraph}
                    className="text-base leading-8 text-gray-700 dark:text-gray-200"
                  >
                    {paragraph}
                  </p>
                ))}
                {section.bullets && section.bullets.length > 0 && (
                  <ul className="list-disc space-y-2 pl-6 text-base leading-7 text-gray-700 dark:text-gray-200">
                    {section.bullets.map((bullet) => (
                      <li key={bullet}>{bullet}</li>
                    ))}
                  </ul>
                )}
              </section>
            ))}
          </div>

          <section className="mt-12 rounded-3xl border border-gray-200 bg-gray-50 p-6 dark:border-white/10 dark:bg-white/5">
            <h3 className="text-lg font-black text-gray-900 dark:text-white">
              Continue reading
            </h3>
            <div className="mt-4 grid gap-3 md:grid-cols-2">
              {relatedArticles.map((related) => (
                <Link
                  key={related.slug}
                  href={`/lnat/${related.slug}`}
                  className="group rounded-2xl border border-gray-200 bg-white p-4 transition hover:border-purple-300 hover:shadow-sm dark:border-white/10 dark:bg-[#111827] dark:hover:border-purple-400/40"
                >
                  <h4 className="font-bold text-gray-900 dark:text-white">
                    {related.title}
                  </h4>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    {related.summary}
                  </p>
                  <span className="mt-3 inline-flex items-center gap-1 text-xs font-bold uppercase tracking-wider text-purple-600 dark:text-purple-300">
                    Read article <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </Link>
              ))}
            </div>
          </section>

          <section className="mt-8 rounded-3xl border border-[#D5BF76]/30 bg-[#123733] p-6 text-white dark:bg-[#102B28]">
            <h3 className="text-xl font-black">Ready to practise with structure?</h3>
            <p className="mt-2 text-sm text-teal-100/90">
              Start your LNAT workflow and apply these methods in timed practice.
              No promises of outcomes, just disciplined preparation.
            </p>
            <div className="mt-4 flex flex-wrap gap-3">
              <Link
                href="/lnat/login"
                className="rounded-xl bg-[#D5BF76] px-5 py-2.5 text-sm font-black text-[#123733] transition hover:brightness-105"
              >
                Start Trial
              </Link>
              <Link
                href="/lnat/articles"
                className="rounded-xl border border-white/30 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
              >
                Back to Article Hub
              </Link>
              <Link
                href="/lnat-preparation"
                className="rounded-xl border border-white/30 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
              >
                LNAT Preparation Page
              </Link>
              <Link
                href="/lnat/dashboard"
                className="rounded-xl border border-white/30 px-5 py-2.5 text-sm font-bold text-white transition hover:bg-white/10"
              >
                LNAT HQ
              </Link>
            </div>
          </section>
        </article>
      </main>
    </>
  );
}
